<script setup lang="ts">
import { useForm } from "@inertiajs/vue3"
import { computed, watch } from "vue"
import route from "ziggy-js"
import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { useToast } from "@/components/ui/toast/use-toast"

type Branch = {
    id: number
    name: string
}

type Employee = {
    id: number
    employee_number: string
    branch_id: number | null
    name: string
    title: string
    status: string
    email: string
    mobile: string | null
    address: string
    birthday: string | null
    date_started_at: string | null
    sss_number: string | null
    prn_number: string | null
    mid_number: string | null
    tin_number: string | null
    is_active: boolean
    has_benefits: boolean
    rate_per_hour: number
    full_shift_rate: number
}

const props = defineProps<{
    open: boolean
    employee: Employee | null
    branches: Branch[]
}>()

const emit = defineEmits<{
    "update:open": [value: boolean]
}>()

const { toast } = useToast()

const titleOptions = ["Barista", "Cashier", "Kitchen", "Co-owner / General Manager"]

const titles = computed(() => {
    const current = props.employee?.title

    if (current && !titleOptions.includes(current)) {
        return [current, ...titleOptions]
    }

    return titleOptions
})

const statusOptions = computed(() => {
    const defaults = ["Part Time", "Full Time"]
    const current = props.employee?.status

    if (current && !defaults.includes(current)) {
        return [current, ...defaults]
    }

    return defaults
})

const form = useForm({
    branch_id: "none",
    name: "",
    title: "",
    status: "Part Time",
    email: "",
    mobile: "",
    address: "",
    birthday: "",
    date_started_at: "",
    sss_number: "",
    prn_number: "",
    mid_number: "",
    tin_number: "",
    is_active: false,
    has_benefits: false,
    rate_per_hour: "",
    full_shift_rate: "",
})

watch(
    () => [props.open, props.employee],
    () => {
        if (!props.open || !props.employee) {
            return
        }

        form.clearErrors()
        form.branch_id = props.employee.branch_id ? String(props.employee.branch_id) : "none"
        form.name = props.employee.name
        form.title = props.employee.title
        form.status = props.employee.status
        form.email = props.employee.email
        form.mobile = props.employee.mobile ?? ""
        form.address = props.employee.address
        form.birthday = props.employee.birthday ?? ""
        form.date_started_at = props.employee.date_started_at ?? ""
        form.sss_number = props.employee.sss_number ?? ""
        form.prn_number = props.employee.prn_number ?? ""
        form.mid_number = props.employee.mid_number ?? ""
        form.tin_number = props.employee.tin_number ?? ""
        form.is_active = Boolean(props.employee.is_active)
        form.has_benefits = Boolean(props.employee.has_benefits)
        form.rate_per_hour = String(props.employee.rate_per_hour)
        form.full_shift_rate = String(props.employee.full_shift_rate)
    }
)

function close() {
    emit("update:open", false)
}

function submit() {
    if (!props.employee) {
        return
    }

    form.put(route("employees.update", props.employee.id), {
        preserveScroll: true,
        onSuccess() {
            close()
            toast({ description: "Employee updated." })
        },
    })
}
</script>

<template>
    <Dialog :open="open" @update:open="emit('update:open', $event)">
        <DialogContent class="max-h-[90vh] max-w-2xl overflow-y-auto">
            <form class="space-y-6" @submit.prevent="submit">
                <DialogHeader>
                    <DialogTitle>Edit employee</DialogTitle>
                    <DialogDescription>
                        Update profile, pay, and government IDs for {{ employee?.name ?? "this employee" }}.
                    </DialogDescription>
                </DialogHeader>

                <section class="space-y-3">
                    <h3 class="text-sm font-semibold text-gray-900">Profile</h3>
                    <div class="grid gap-3 sm:grid-cols-2">
                        <div class="space-y-2">
                            <Label>Employee number</Label>
                            <Input :model-value="employee?.employee_number ?? ''" disabled class="font-mono tracking-wide" />
                        </div>
                        <div class="space-y-2 sm:col-span-2">
                            <Label>Name</Label>
                            <Input v-model="form.name" />
                            <p v-if="form.errors.name" class="text-sm text-destructive">{{ form.errors.name }}</p>
                        </div>
                        <div class="space-y-2">
                            <Label>Email</Label>
                            <Input v-model="form.email" type="email" />
                            <p v-if="form.errors.email" class="text-sm text-destructive">{{ form.errors.email }}</p>
                        </div>
                        <div class="space-y-2">
                            <Label>Mobile</Label>
                            <Input v-model="form.mobile" />
                            <p v-if="form.errors.mobile" class="text-sm text-destructive">{{ form.errors.mobile }}</p>
                        </div>
                        <div class="space-y-2 sm:col-span-2">
                            <Label>Address</Label>
                            <Input v-model="form.address" />
                            <p v-if="form.errors.address" class="text-sm text-destructive">{{ form.errors.address }}</p>
                        </div>
                        <div class="space-y-2">
                            <Label>Birthday</Label>
                            <Input v-model="form.birthday" type="date" />
                            <p v-if="form.errors.birthday" class="text-sm text-destructive">{{ form.errors.birthday }}</p>
                        </div>
                        <div class="space-y-2">
                            <Label>Date started</Label>
                            <Input v-model="form.date_started_at" type="date" />
                            <p v-if="form.errors.date_started_at" class="text-sm text-destructive">
                                {{ form.errors.date_started_at }}
                            </p>
                        </div>
                    </div>
                </section>

                <section class="space-y-3">
                    <h3 class="text-sm font-semibold text-gray-900">Employment</h3>
                    <div class="grid gap-3 sm:grid-cols-2">
                        <div class="space-y-2">
                            <Label>Title</Label>
                            <Select v-model:model-value="form.title">
                                <SelectTrigger>
                                    <SelectValue placeholder="Select title" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem v-for="title in titles" :key="title" :value="title">
                                        {{ title }}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            <p v-if="form.errors.title" class="text-sm text-destructive">{{ form.errors.title }}</p>
                        </div>
                        <div class="space-y-2">
                            <Label>Status</Label>
                            <Select v-model:model-value="form.status">
                                <SelectTrigger>
                                    <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem v-for="status in statusOptions" :key="status" :value="status">
                                        {{ status }}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            <p v-if="form.errors.status" class="text-sm text-destructive">{{ form.errors.status }}</p>
                        </div>
                        <div class="space-y-2 sm:col-span-2">
                            <Label>Branch</Label>
                            <Select v-model:model-value="form.branch_id">
                                <SelectTrigger>
                                    <SelectValue placeholder="Select branch" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">No branch</SelectItem>
                                    <SelectItem v-for="branch in branches" :key="branch.id" :value="String(branch.id)">
                                        {{ branch.name }}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            <p v-if="form.errors.branch_id" class="text-sm text-destructive">{{ form.errors.branch_id }}</p>
                        </div>
                    </div>
                    <div class="grid gap-3 sm:grid-cols-2">
                        <Label class="flex items-center gap-2 text-sm font-normal">
                            <Switch :checked="form.is_active" @update:checked="form.is_active = $event" />
                            Active
                        </Label>
                        <Label class="flex items-center gap-2 text-sm font-normal">
                            <Switch :checked="form.has_benefits" @update:checked="form.has_benefits = $event" />
                            Has benefits
                        </Label>
                    </div>
                </section>

                <section class="space-y-3">
                    <h3 class="text-sm font-semibold text-gray-900">Pay</h3>
                    <div class="grid gap-3 sm:grid-cols-2">
                        <div class="space-y-2">
                            <Label>Hourly rate (₱)</Label>
                            <Input v-model="form.rate_per_hour" type="number" min="0" step="0.01" />
                            <p v-if="form.errors.rate_per_hour" class="text-sm text-destructive">
                                {{ form.errors.rate_per_hour }}
                            </p>
                        </div>
                        <div class="space-y-2">
                            <Label>Daily rate (₱)</Label>
                            <Input v-model="form.full_shift_rate" type="number" min="0" step="0.01" />
                            <p v-if="form.errors.full_shift_rate" class="text-sm text-destructive">
                                {{ form.errors.full_shift_rate }}
                            </p>
                        </div>
                    </div>
                </section>

                <section class="space-y-3">
                    <h3 class="text-sm font-semibold text-gray-900">Government IDs</h3>
                    <div class="grid gap-3 sm:grid-cols-2">
                        <div class="space-y-2">
                            <Label>SSS</Label>
                            <Input v-model="form.sss_number" />
                            <p v-if="form.errors.sss_number" class="text-sm text-destructive">{{ form.errors.sss_number }}</p>
                        </div>
                        <div class="space-y-2">
                            <Label>PhilHealth</Label>
                            <Input v-model="form.prn_number" />
                            <p v-if="form.errors.prn_number" class="text-sm text-destructive">{{ form.errors.prn_number }}</p>
                        </div>
                        <div class="space-y-2">
                            <Label>Pag-IBIG</Label>
                            <Input v-model="form.mid_number" />
                            <p v-if="form.errors.mid_number" class="text-sm text-destructive">{{ form.errors.mid_number }}</p>
                        </div>
                        <div class="space-y-2">
                            <Label>TIN</Label>
                            <Input v-model="form.tin_number" />
                            <p v-if="form.errors.tin_number" class="text-sm text-destructive">{{ form.errors.tin_number }}</p>
                        </div>
                    </div>
                </section>

                <DialogFooter>
                    <Button type="button" variant="outline" @click="close">Cancel</Button>
                    <Button type="submit" :disabled="form.processing">Save changes</Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>
